import React from "react";
import { Column as RTColumn, ColumnInstance, useSortBy, useTable } from "react-table";

import { SearchLoader } from "../loaders/searchLoader/SearchLoader";
import styles from "./Table.module.scss";

export type Column<D extends {}> = RTColumn<D> & {
  collapse?: boolean;
};

interface Props<D extends {}> {
  columns: Column<D>[];
  data: D[];
  onRowClick?: (data: D) => void;
  noResultsText?: string;
  sortBy?: {
    id: string;
    desc: boolean;
  };
}

export const Table = <D extends object = {}>({ columns, data, onRowClick, noResultsText, sortBy }: Props<D>) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<D>(
    {
      columns,
      data,
      initialState: {
        ...(sortBy && { sortBy: [sortBy] }),
      },
      autoResetSortBy: false,
    },
    useSortBy
  );

  if (data.length === 0) {
    return (
      <SearchLoader
        text={noResultsText || "Sorry, pas je zoekopdracht aan. Met deze invoer hebben we niets gevonden."}
      />
    );
  }

  const getColumnHeaderStyles = (column: ColumnInstance<D>) => {
    const classNames = `${styles["table-header-icon"]}`;
    if (column.isSorted) {
      if (column.isSortedDesc) {
        return `${classNames} icon-arrow-down`;
      }
      return `${classNames} icon-arrow-up`;
    }
    return `${classNames} ${styles["table-header-icon-placeholder"]} icon-arrow-up`;
  };

  return (
    <div className={styles.table_wrapper}>
      <table {...getTableProps()} className={styles.table}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id ?? "headerGroup"}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps({
                    ...column.getSortByToggleProps(),
                    className: column.collapse ? styles.collapse : "",
                  })}
                  key={column.id}
                  tabIndex={column.canSort === false ? -1 : 0}
                  onKeyPress={(event) => {
                    if (column.canSort !== false && (event.key === "Enter" || event.key === " ")) {
                      column.toggleSortBy();
                    }
                  }}
                >
                  {column.render("Header")}
                  <span id={`${column.Header}-toggle-icon`} className={getColumnHeaderStyles(column)} />
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={onRowClick ? styles["table-clickable-row"] : ""}
                tabIndex={onRowClick ? 0 : -1}
                onKeyPress={(event) => {
                  if (onRowClick && (event.key === "Enter" || event.key === " ")) {
                    onRowClick(row.original);
                  }
                }}
              >
                {row.cells.map((cell, index) => (
                  <td
                    {...cell.getCellProps({
                      className: cell.column.collapse ? styles.collapse : "",
                    })}
                    key={`${cell.value}_${index}`}
                  >
                    {cell.render("Cell")}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
